import { useState, useEffect } from 'react'
import {
  Grid,
  Box,
  Typography,
  Switch,
  FormControlLabel,
  Divider,
  TextField,
  Avatar,
  Badge,
  CircularProgress,
  IconButton,
} from '@mui/material'
import { PhotoCamera } from '@mui/icons-material'
import { PageHeader, Card, Button, Modal } from '@components/common'
import { useAuth } from '@context/AuthContext'
import api from '@services/api'
import { toast } from 'react-toastify'

const Settings = () => {
  const { user, profile, refreshProfile } = useAuth()
  const [notifications, setNotifications] = useState({
    emailWorkOrders: true,
    emailMaintenanceDue: true,
    emailInventoryAlerts: true,
    pushWorkOrders: true,
    pushMaintenanceDue: true,
  })
  const [editProfileOpen, setEditProfileOpen] = useState(false)
  const [changePasswordOpen, setChangePasswordOpen] = useState(false)
  const [saving, setSaving] = useState(false)
  const [uploadingPicture, setUploadingPicture] = useState(false)
  const [profileFormData, setProfileFormData] = useState({
    first_name: '',
    last_name: '',
    phone_number: '',
  })
  const [passwordFormData, setPasswordFormData] = useState({
    old_password: '',
    new_password: '',
    confirm_password: '',
  })

  useEffect(() => {
    if (user) {
      setProfileFormData({
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        phone_number: profile?.phone_number || user.profile?.phone_number || '',
      })
    }
  }, [user, profile])

  useEffect(() => {
    const loadPreferences = async () => {
      try {
        const response = await api.get('/notifications/preferences/')
        setNotifications({
          emailWorkOrders: response.data.email_work_orders ?? true,
          emailMaintenanceDue: response.data.email_maintenance_due ?? true,
          emailInventoryAlerts: response.data.email_inventory_alerts ?? true,
          pushWorkOrders: response.data.push_work_orders ?? true,
          pushMaintenanceDue: response.data.push_maintenance_due ?? true,
        })
      } catch (error) {
        console.error('Error loading preferences:', error)
      }
    }
    loadPreferences()
  }, [])

  const handleNotificationChange = (key) => {
    setNotifications((prev) => ({ ...prev, [key]: !prev[key] }))
  }

  const handleSavePreferences = async () => {
    setSaving(true)
    try {
      await api.patch('/notifications/preferences/', {
        email_work_orders: notifications.emailWorkOrders,
        email_maintenance_due: notifications.emailMaintenanceDue,
        email_inventory_alerts: notifications.emailInventoryAlerts,
        push_work_orders: notifications.pushWorkOrders,
        push_maintenance_due: notifications.pushMaintenanceDue,
      })
      toast.success('Preferences saved successfully')
    } catch (error) {
      toast.error('Failed to save preferences')
    } finally {
      setSaving(false)
    }
  }

  const handleOpenEditProfile = () => {
    setProfileFormData({
      first_name: user?.first_name || '',
      last_name: user?.last_name || '',
      phone_number: profile?.phone_number || user?.profile?.phone_number || '',
    })
    setEditProfileOpen(true)
  }

  const handleSaveProfile = async () => {
    setSaving(true)
    try {
      await api.patch('/auth/profile/', profileFormData)
      toast.success('Profile updated successfully')
      setEditProfileOpen(false)
      if (refreshProfile) {
        await refreshProfile()
      }
    } catch (error) {
      toast.error('Failed to update profile')
    } finally {
      setSaving(false)
    }
  }

  const handleOpenChangePassword = () => {
    setPasswordFormData({
      old_password: '',
      new_password: '',
      confirm_password: '',
    })
    setChangePasswordOpen(true)
  }

  const handleChangePassword = async () => {
    if (!passwordFormData.old_password || !passwordFormData.new_password) {
      toast.error('Please fill in all fields')
      return
    }
    if (passwordFormData.new_password !== passwordFormData.confirm_password) {
      toast.error('New passwords do not match')
      return
    }
    if (passwordFormData.new_password.length < 8) {
      toast.error('Password must be at least 8 characters')
      return
    }
    setSaving(true)
    try {
      await api.post('/auth/change-password/', {
        old_password: passwordFormData.old_password,
        new_password: passwordFormData.new_password,
      })
      toast.success('Password changed successfully')
      setChangePasswordOpen(false)
    } catch (error) {
      const errorMessage = error.response?.data?.old_password?.[0]
        || error.response?.data?.error
        || 'Failed to change password'
      toast.error(errorMessage)
    } finally {
      setSaving(false)
    }
  }

  const handleProfilePictureChange = async (event) => {
    const file = event.target.files?.[0]
    if (!file) return

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file')
      return
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be less than 5MB')
      return
    }

    setUploadingPicture(true)
    try {
      const formData = new FormData()
      formData.append('profile_picture', file)

      await api.patch('/auth/profile/', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      })
      toast.success('Profile picture updated successfully')
      if (refreshProfile) {
        await refreshProfile()
      }
    } catch (error) {
      console.error('Error uploading profile picture:', error)
      toast.error('Failed to upload profile picture')
    } finally {
      setUploadingPicture(false)
      // Reset file input
      event.target.value = ''
    }
  }

  const getInitials = () => {
    if (user?.first_name && user?.last_name) {
      return `${user.first_name[0]}${user.last_name[0]}`.toUpperCase()
    }
    return user?.username?.[0]?.toUpperCase() || 'U'
  }

  return (
    <Box>
      <PageHeader
        title="Settings"
        subtitle="Manage your profile and preferences"
        breadcrumbs={[
          { label: 'Dashboard', path: '/dashboard' },
          { label: 'Settings' },
        ]}
      />

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card title="Profile Information">
            <Box display="flex" flexDirection="column" gap={2}>
              {/* Profile Picture Section */}
              <Box display="flex" alignItems="center" gap={3} mb={2}>
                <Badge
                  overlap="circular"
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                  badgeContent={
                    uploadingPicture ? (
                      <CircularProgress size={20} />
                    ) : (
                      <IconButton
                        component="label"
                        sx={{
                          bgcolor: 'primary.main',
                          color: 'white',
                          width: 32,
                          height: 32,
                          '&:hover': { bgcolor: 'primary.dark' },
                        }}
                      >
                        <PhotoCamera fontSize="small" />
                        <input
                          type="file"
                          hidden
                          accept="image/*"
                          onChange={handleProfilePictureChange}
                        />
                      </IconButton>
                    )
                  }
                >
                  <Avatar
                    src={profile?.profile_picture_url || profile?.profile_picture}
                    sx={{ width: 80, height: 80, fontSize: '1.5rem' }}
                  >
                    {getInitials()}
                  </Avatar>
                </Badge>
                <Box>
                  <Typography variant="subtitle1" fontWeight={600}>
                    {user?.first_name ? `${user.first_name} ${user.last_name || ''}` : user?.username}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {profile?.job_title || 'No job title set'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Click the camera icon to change your photo
                  </Typography>
                </Box>
              </Box>

              <Divider />

              <Box>
                <Typography variant="caption" color="text.secondary">Username</Typography>
                <Typography variant="body1">{user?.username}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Email</Typography>
                <Typography variant="body1">{user?.email || '-'}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Name</Typography>
                <Typography variant="body1">
                  {user?.first_name ? `${user.first_name} ${user.last_name || ''}` : '-'}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Phone Number</Typography>
                <Typography variant="body1">{profile?.phone_number || '-'}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Department</Typography>
                <Typography variant="body1">{profile?.department_name || '-'}</Typography>
              </Box>
              <Divider sx={{ my: 1 }} />
              <Button variant="outlined" onClick={handleOpenEditProfile}>Edit Profile</Button>
            </Box>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card title="Notification Preferences">
            <Box display="flex" flexDirection="column" gap={1}>
              <Typography variant="subtitle2" color="text.secondary" mb={1}>Email Notifications</Typography>
              <FormControlLabel
                control={<Switch checked={notifications.emailWorkOrders} onChange={() => handleNotificationChange('emailWorkOrders')} />}
                label="Work order updates"
              />
              <FormControlLabel
                control={<Switch checked={notifications.emailMaintenanceDue} onChange={() => handleNotificationChange('emailMaintenanceDue')} />}
                label="Maintenance reminders"
              />
              <FormControlLabel
                control={<Switch checked={notifications.emailInventoryAlerts} onChange={() => handleNotificationChange('emailInventoryAlerts')} />}
                label="Inventory alerts"
              />
              <Typography variant="subtitle2" color="text.secondary" mt={2} mb={1}>Push Notifications</Typography>
              <FormControlLabel
                control={<Switch checked={notifications.pushWorkOrders} onChange={() => handleNotificationChange('pushWorkOrders')} />}
                label="Work order updates"
              />
              <FormControlLabel
                control={<Switch checked={notifications.pushMaintenanceDue} onChange={() => handleNotificationChange('pushMaintenanceDue')} />}
                label="Maintenance reminders"
              />
              <Box mt={2}>
                <Button onClick={handleSavePreferences} loading={saving}>Save Preferences</Button>
              </Box>
            </Box>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card title="Security">
            <Typography variant="body2" color="text.secondary" mb={2}>
              Keep your account secure by using a strong password.
            </Typography>
            <Button variant="outlined" onClick={handleOpenChangePassword}>Change Password</Button>
          </Card>
        </Grid>
      </Grid>

      {/* Edit Profile Modal */}
      <Modal
        open={editProfileOpen}
        onClose={() => setEditProfileOpen(false)}
        title="Edit Profile"
        maxWidth="sm"
        actions={
          <>
            <Button variant="outlined" onClick={() => setEditProfileOpen(false)}>Cancel</Button>
            <Button onClick={handleSaveProfile} loading={saving}>Save</Button>
          </>
        }
      >
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="First Name"
              value={profileFormData.first_name}
              onChange={(e) => setProfileFormData({ ...profileFormData, first_name: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Last Name"
              value={profileFormData.last_name}
              onChange={(e) => setProfileFormData({ ...profileFormData, last_name: e.target.value })}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Phone Number"
              value={profileFormData.phone_number}
              onChange={(e) => setProfileFormData({ ...profileFormData, phone_number: e.target.value })}
            />
          </Grid>
        </Grid>
      </Modal>

      {/* Change Password Modal */}
      <Modal
        open={changePasswordOpen}
        onClose={() => setChangePasswordOpen(false)}
        title="Change Password"
        maxWidth="sm"
        actions={
          <>
            <Button variant="outlined" onClick={() => setChangePasswordOpen(false)}>Cancel</Button>
            <Button onClick={handleChangePassword} loading={saving}>Change Password</Button>
          </>
        }
      >
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              type="password"
              label="Current Password"
              value={passwordFormData.old_password}
              onChange={(e) => setPasswordFormData({ ...passwordFormData, old_password: e.target.value })}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              type="password"
              label="New Password"
              value={passwordFormData.new_password}
              onChange={(e) => setPasswordFormData({ ...passwordFormData, new_password: e.target.value })}
              helperText="Must be at least 8 characters"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              type="password"
              label="Confirm New Password"
              value={passwordFormData.confirm_password}
              onChange={(e) => setPasswordFormData({ ...passwordFormData, confirm_password: e.target.value })}
            />
          </Grid>
        </Grid>
      </Modal>
    </Box>
  )
}

export default Settings
